import { getHandle } from 'config/collections/utils/get-handle';
import {
  isAgeSegmentation,
  isBreedSegmentation,
  isNutritionSegmentation,
  SegmentationData,
  SegmentationIds,
} from 'constants/segmentation';
import { Region } from 'constants/shop';
import { InputProductType } from 'interfaces/api-input/input-product-type';
import { CollectionFilter, FilterConfig } from 'interfaces/collection-filter';
import FILTER_CONFIG, { getFilterConfigByHandle } from 'modules/collection/state/filter-config';
import { getFilterGroupOrderByHandle } from 'modules/collection/state/filter-order';
import { CDP_MAPPING } from 'utils/api/smartCollection';
import { CdpType, FilterOrderMapper, PetType } from '../const';

/**
 * Check if the collection is snack collection
 */
export const isSnackCdp = (handle: string): boolean =>
  CDP_MAPPING.filter((config) => config.productType === InputProductType.Snacks).some((config) =>
    config.url.some((url) => url.replace(/^\/collections\//, '') === handle)
  );

export const getKeyBySegmentationId = ({
  segmentation,
}: {
  segmentation: SegmentationIds;
}): (typeof CdpType)[keyof typeof CdpType] | undefined => {
  if (isBreedSegmentation(segmentation)) {
    return CdpType.BreedCdp;
  } else if (isAgeSegmentation(segmentation)) {
    return CdpType.AgeTypeCdp;
  } else if (isNutritionSegmentation(segmentation)) {
    return CdpType.NeedCdp;
  }
};

// Create CdpTypeMapper
export const createCdpTypeMapper = () => {
  const CdpTypeMapper = new Map<string, (typeof CdpType)[keyof typeof CdpType]>();

  // set segmentation related cdp
  SegmentationData.segmentation.map((segment) => {
    const cdpTypeKey = getKeyBySegmentationId({ segmentation: segment.id });
    if (cdpTypeKey) {
      segment.redirectPages.forEach((url) => {
        const handle = url.replace(/^\/collections\//, '');
        CdpTypeMapper.set(handle, cdpTypeKey);
      });
    }
  });

  // set general cdp
  CDP_MAPPING.map((v) => v.url).forEach((urls) => {
    urls.forEach((url) => {
      const handle = url.replace(/^\/collections\//, '');
      CdpTypeMapper.set(handle, CdpType.FoodTypeCdp);
    });
  });

  const SPECIAL_HANDLE = [CdpType.Home, CdpType.YourPetProfile];
  SPECIAL_HANDLE.forEach((cdpType) => {
    CdpTypeMapper.set(cdpType, cdpType);
  });

  return CdpTypeMapper;
};

export const getFilterOrder = ({
  isPetProfileApplied,
  handle,
  region,
  petType,
}: {
  isPetProfileApplied?: boolean;
  handle: string;
  region?: Region;
  petType: PetType;
}) => {
  const cdpTypeMapper = createCdpTypeMapper();
  const cdpType = cdpTypeMapper.get(handle);

  if (!cdpType) {
    // If the CDP type is not found, fall back to the collection specific group order
    const groupOrder = getFilterGroupOrderByHandle(handle, region);
    if (groupOrder) {
      return groupOrder;
    }

    const baseConfig = getFilterConfigByHandle(handle, region);
    return baseConfig ? Object.keys(baseConfig) : undefined;
  }

  let filters = FilterOrderMapper[cdpType][petType];

  // Add snack-type filter for snack collections
  if (isSnackCdp(handle)) {
    if (Array.isArray(filters[0])) {
      filters = filters.map((filterSet) => ['snack-type', ...filterSet]);
    } else {
      filters = ['snack-type', ...(filters as Array<CollectionFilter>)];
    }
  }

  if (filters.some((v) => Array.isArray(v)) && typeof isPetProfileApplied === 'boolean') {
    const index = isPetProfileApplied ? 1 : 0;
    return (filters as Array<Array<CollectionFilter>>)[index];
  }

  return filters as Array<CollectionFilter>;
};

export const reorderObject = (obj: FilterConfig, keysOrder: string[] | undefined): FilterConfig => {
  if (!Array.isArray(keysOrder)) return obj;

  const reorderedObj: Partial<FilterConfig> = {};

  keysOrder.forEach((key) => {
    if (key in obj) {
      reorderedObj[key] = obj[key];
    }
  });

  return reorderedObj as FilterConfig;
};

/**
 * Modify config order by definitions
 */
export const modifyConfig = ({
  filterConfig,
  handle,
  isPetProfileApplied,
  region,
  petType,
}: {
  filterConfig: FilterConfig;
  handle: string;
  isPetProfileApplied?: boolean;
  region?: Region;
  petType: PetType;
}): FilterConfig => {
  const filterOrder = getFilterOrder({
    handle,
    isPetProfileApplied,
    region,
    petType,
  });

  if (!filterOrder) {
    return filterConfig;
  }

  // Ensure we have a flat array to work with
  let reorderConfig = Array.isArray(filterOrder)
    ? Array.isArray(filterOrder[0])
      ? [...filterOrder[0]]
      : [...filterOrder]
    : [];

  const temp = { ...filterConfig };

  // Required for marketing usage
  // TODO: check with marketing team why do we need this
  if (handle === getHandle({ key: 'catsWetFood', region })) {
    const catWetFoodFilters = FILTER_CONFIG.catsWetFood;
    if (catWetFoodFilters?.line) {
      reorderConfig = [...reorderConfig.slice(0, 1), 'line', ...reorderConfig.slice(1)];
      temp.line = catWetFoodFilters.line;
    }
  }

  if (handle === getHandle({ key: 'dogsDryFood', region })) {
    const dogDryFoodFilters = FILTER_CONFIG.dogsDryFood;
    if (dogDryFoodFilters?.line) {
      reorderConfig = ['line', ...reorderConfig];
      temp.line = dogDryFoodFilters.line;
    }
  }

  // Merge collection-specific desires (e.g. the hypocare "Hypo" chip) into the shared set.
  const perHandleDesires = getFilterConfigByHandle(handle, region)?.desires;
  if (perHandleDesires) {
    const baseDesires = temp.desires;
    const extraTags = perHandleDesires.tags.filter((tag) => !baseDesires?.tags.includes(tag));
    if (extraTags.length) {
      temp.desires = {
        ...baseDesires,
        tags: [...(baseDesires?.tags ?? []), ...extraTags],
      };
    }
  }

  return reorderObject(temp, reorderConfig);
};
